// Floating "Report a problem" button that opens a small modal to submit a
// support ticket. Visible on every authenticated page. Ticket data:
//   category · subject · message  (+ auto: current URL, user agent)
//
// After successful submit the modal shows a confirmation with the ticket
// id and closes on its own after 3 seconds.
import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { LifeBuoy, Send, Check } from 'lucide-react';

const CATEGORIES = [
  { value: 'bug',      label: 'Bug — something is broken' },
  { value: 'question', label: 'Question — I need help using something' },
  { value: 'feature',  label: 'Feature request' },
  { value: 'other',    label: 'Other' },
];

export default function SupportWidget() {
  const { api, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: 'bug', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [ticketId, setTicketId] = useState(null);

  if (!isAuthenticated) return null;

  const reset = () => {
    setForm({ category: 'bug', subject: '', message: '' });
    setTicketId(null);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.subject.trim() || form.subject.trim().length < 3) {
      toast.error('Subject is too short'); return;
    }
    if (!form.message.trim() || form.message.trim().length < 5) {
      toast.error('Please describe the problem in a few words'); return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/support/tickets', {
        category: form.category,
        subject: form.subject.trim(),
        message: form.message.trim(),
        page_url: typeof window !== 'undefined' ? window.location.href : null,
        user_agent: typeof window !== 'undefined' ? window.navigator.userAgent : null,
      });
      setTicketId(data.id);
      toast.success('Ticket submitted');
      setTimeout(() => { setOpen(false); reset(); }, 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Report a problem"
        data-testid="support-widget-trigger"
        className="fixed z-40 bottom-24 right-6 h-12 w-12 rounded-full bg-cyan-600 hover:bg-cyan-500 text-white shadow-lg shadow-cyan-900/50 flex items-center justify-center transition-transform hover:scale-105"
      >
        <LifeBuoy className="w-5 h-5" />
      </button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg" data-testid="support-widget-dialog">
          {ticketId ? (
            <div className="py-8 text-center">
              <div className="w-14 h-14 rounded-full bg-emerald-950/60 border border-emerald-800 flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-emerald-400" />
              </div>
              <h3 className="text-white text-lg font-semibold">Thanks — we're on it.</h3>
              <p className="text-slate-400 text-sm mt-1.5">
                Ticket <code className="text-cyan-300">{ticketId}</code> was created.
                We'll follow up by email as soon as we can.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <LifeBuoy className="w-5 h-5 text-cyan-400" /> Report a problem
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  Tell us what happened. Your name, email and the page URL are attached automatically.
                </DialogDescription>
              </DialogHeader>
              <div>
                <Label className="text-slate-300">Category</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}
                >
                  <SelectTrigger className="bg-slate-950 border-slate-800 mt-1.5" data-testid="support-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Subject</Label>
                <Input
                  value={form.subject}
                  onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
                  className="bg-slate-950 border-slate-800 mt-1.5"
                  placeholder="Short summary"
                  data-testid="support-subject-input"
                  maxLength={200}
                />
              </div>
              <div>
                <Label className="text-slate-300">Message</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                  className="bg-slate-950 border-slate-800 mt-1.5 min-h-[110px]"
                  placeholder="What were you doing, what did you expect, what actually happened?"
                  data-testid="support-message-input"
                  maxLength={5000}
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}
                  className="text-slate-400 hover:text-slate-200">
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}
                  className="bg-cyan-600 hover:bg-cyan-500 text-white"
                  data-testid="support-submit-btn">
                  <Send className="w-4 h-4 mr-1.5" />
                  {submitting ? 'Sending…' : 'Submit'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
